// Session Storage Helper for Bite Balance Assessment Data
// Optimized for performance and type safety

// Medication interface - Updated for clean format
interface MedicationDetail {
  name: string;
  doses: Array<{
    frequency: string;
    time: string;
  }>;
}

// Insulin interface
interface InsulinDetail {
  dose_type: string;
  insulin_type: string;
  timing: string;
  dose: string;
  brand: string;
}

// Main assessment data interface - exact API structure
export interface AssessmentApiData {
  // Personal Info
  gender?: string;
  race?: string;
  date_of_birth?: string;

  // Height (multiple units support)
  height_in?: string;
  height_ft?: number;
  height_inches?: number;
  height_cm?: number;

  // Weight (multiple units support)
  weight_in?: string;
  weight_kg?: number;
  weight_lbs?: number;

  // Target Weight (optional, multiple units support)
  target_weight_in?: string;
  target_weight_kg?: number;
  target_weight_lbs?: number;

  // Health Info
  diabetes_type?: string;
  weight_loss_goal?: string;
  weight_loss_reasons?: string;
  activity_level?: string;
  diet_adherence?: string;
  intermittent_fasting?: string;
  fasting_window_type?: string;

  // Schedules
  meal_times?: string[];

  // Medical Details
  medication_details?: MedicationDetail[];
  insulin_details?: InsulinDetail[];

  // Status
  is_completed?: boolean;
  completion_date?: string | null;
  assessment_progress?: number;
}

// Storage configuration
const STORAGE_KEY = "glucera_assessment_data";
const BACKUP_KEY = "glucera_assessment_backup";

// Cache for better performance
let _cache: AssessmentApiData | null = null;
let _lastCacheTime = 0;

// Utility functions
const isValidValue = (value: any): boolean => {
  return value !== null && value !== undefined && value !== "";
};

const safeJsonParse = (str: string): AssessmentApiData => {
  try {
    return JSON.parse(str) ?? {};
  } catch {
    return {};
  }
};

const safeJsonStringify = (data: any): string => {
  try {
    return JSON.stringify(data);
  } catch {
    return "{}";
  }
};

export const SessionStorageHelper = {
  // Enhanced save with backup and validation
  saveField: (fieldName: keyof AssessmentApiData, value: any) => {
    try {
      const existingData = SessionStorageHelper.getAllData();

      // If value is null, remove the field instead of storing null
      if (value === null) {
        const updatedData = { ...existingData };
        delete updatedData[fieldName];

        // Save with backup
        const dataString = safeJsonStringify(updatedData);
        sessionStorage.setItem(STORAGE_KEY, dataString);
        sessionStorage.setItem(BACKUP_KEY, dataString);

        // Update cache
        _cache = updatedData;
        _lastCacheTime = Date.now();

        return true;
      }

      // For valid values, proceed with normal save
      if (!isValidValue(value)) {
        return false;
      }

      const updatedData = { ...existingData, [fieldName]: value };

      // Save with backup
      const dataString = safeJsonStringify(updatedData);
      sessionStorage.setItem(STORAGE_KEY, dataString);
      sessionStorage.setItem(BACKUP_KEY, dataString);

      // Update cache
      _cache = updatedData;
      _lastCacheTime = Date.now();

      return true;
    } catch (_error) {
      return false;
    }
  },

  // Enhanced batch save with transaction-like behavior
  saveMultipleFields: (data: Partial<AssessmentApiData>) => {
    if (!data || Object.keys(data).length === 0) {
      return false;
    }

    try {
      const existingData = SessionStorageHelper.getAllData();
      const updatedData = { ...existingData, ...data };

      const dataString = safeJsonStringify(updatedData);
      sessionStorage.setItem(STORAGE_KEY, dataString);
      sessionStorage.setItem(BACKUP_KEY, dataString);

      // Update cache
      _cache = updatedData;
      _lastCacheTime = Date.now();

      return true;
    } catch (_error) {
      return false;
    }
  },

  // Get specific field with caching
  getField: (fieldName: keyof AssessmentApiData) => {
    try {
      const data = SessionStorageHelper.getAllData();
      const value = data[fieldName];
      return value;
    } catch (_error) {
      return null;
    }
  },

  // Enhanced getAllData with caching and backup recovery
  getAllData: (): AssessmentApiData => {
    // Use cache if fresh
    const now = Date.now();
    if (_cache && now - _lastCacheTime < 5000) {
      return _cache;
    }

    try {
      // Try main storage
      let dataStr = sessionStorage.getItem(STORAGE_KEY);

      // Fallback to backup if main is corrupted
      if (!dataStr) {
        dataStr = sessionStorage.getItem(BACKUP_KEY);
        if (dataStr) {
          sessionStorage.setItem(STORAGE_KEY, dataStr);
        }
      }

      const data = dataStr ? safeJsonParse(dataStr) : {};

      // Update cache
      _cache = data;
      _lastCacheTime = now;

      return data;
    } catch (_error) {
      _cache = {};
      return {};
    }
  },

  // Enhanced clear with confirmation and backup cleanup
  clearData: () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(BACKUP_KEY);

      // Clear cache
      _cache = null;
      _lastCacheTime = 0;

      return true;
    } catch (_error) {
      return false;
    }
  },

  // Check if data exists
  hasData: (): boolean => {
    const data = SessionStorageHelper.getAllData();
    return Object.keys(data).length > 0;
  },

  // Enhanced progress calculation with field weighting
  getProgress: (): number => {
    const data = SessionStorageHelper.getAllData();

    // Essential fields (higher weight)
    const essentialFields: (keyof AssessmentApiData)[] = [
      "gender",
      "race",
      "date_of_birth",
      "diabetes_type",
    ];

    // Important fields (medium weight)
    const importantFields: (keyof AssessmentApiData)[] = [
      "weight_kg",
      "weight_lbs",
      "height_ft",
      "height_cm",
      "activity_level",
      "diet_adherence",
    ];

    // Optional fields (lower weight)
    const optionalFields: (keyof AssessmentApiData)[] = [
      "weight_loss_goal",
      "intermittent_fasting",
      "meal_times",
    ];

    const essentialScore = essentialFields.filter(field =>
      isValidValue(data[field])
    ).length;
    const importantScore = importantFields.filter(field =>
      isValidValue(data[field])
    ).length;
    const optionalScore = optionalFields.filter(field =>
      isValidValue(data[field])
    ).length;

    // Weighted calculation
    const totalScore =
      essentialScore * 3 + importantScore * 2 + optionalScore * 1;
    const maxScore =
      essentialFields.length * 3 +
      importantFields.length * 2 +
      optionalFields.length * 1;

    const progress = Math.round((totalScore / maxScore) * 100);
    return Math.min(progress, 100);
  },

  // Enhanced validation with better error reporting
  validateRequiredFields: (): {
    isValid: boolean;
    missingFields: string[];
    data: AssessmentApiData;
    validationSummary: Record<string, boolean>;
  } => {
    const data = SessionStorageHelper.getAllData();

    const requiredFields: (keyof AssessmentApiData)[] = [
      "gender",
      "race",
      "date_of_birth",
      "diabetes_type",
    ];

    // Validation summary for debugging
    const validationSummary: Record<string, boolean> = {};
    const missingFields: string[] = [];

    // Check basic required fields
    requiredFields.forEach(field => {
      const isFieldValid = isValidValue(data[field]);
      validationSummary[field] = isFieldValid;

      if (!isFieldValid) {
        missingFields.push(field);
      }
    });

    // Check height requirement (at least one height field)
    const hasHeight =
      isValidValue(data.height_ft) ||
      isValidValue(data.height_inches) ||
      isValidValue(data.height_cm);
    validationSummary["height"] = hasHeight;

    if (!hasHeight) {
      missingFields.push("height");
    }

    // Check weight requirement (at least one weight field)
    const hasWeight =
      isValidValue(data.weight_kg) || isValidValue(data.weight_lbs);
    validationSummary["weight"] = hasWeight;

    if (!hasWeight) {
      missingFields.push("weight");
    }

    const isValid = missingFields.length === 0;
    return { isValid, missingFields, data, validationSummary };
  },

  // Enhanced assessment summary with data normalization
  getAssessmentSummary: (): AssessmentApiData => {
    const data = { ...SessionStorageHelper.getAllData() };

    // Normalize arrays with safe defaults
    data.medication_details = Array.isArray(data.medication_details)
      ? data.medication_details
      : [];

    data.insulin_details = Array.isArray(data.insulin_details)
      ? data.insulin_details
      : [];

    data.meal_times =
      Array.isArray(data.meal_times) && data.meal_times.length > 0
        ? data.meal_times
        : ["10:00", "13:00"];

    // Set metadata
    data.is_completed = false;
    data.completion_date = null;
    data.assessment_progress = SessionStorageHelper.getProgress();

    return data;
  },

  // Enhanced submission with retry logic and better error handling
  submitAssessment: async (retryCount = 3): Promise<any> => {
    // Pre-submission validation with detailed logging
    const validation = SessionStorageHelper.validateRequiredFields();

    if (!validation.isValid) {
      const error = new Error(
        `Validation failed: ${validation.missingFields.join(", ")}`
      );
      throw error;
    }

    // Get normalized data
    const sessionData = SessionStorageHelper.getAssessmentSummary();

    // Dynamic import for code splitting
    const assessmentService = (await import("../services/AssessmentService"))
      .default;

    // Transform and submit with retry logic
    let lastError;
    for (let attempt = 1; attempt <= retryCount; attempt++) {
      try {
        const apiPayload =
          assessmentService.transformSessionDataToPayload(sessionData);

        const response =
          await assessmentService.submitNewAssessment(apiPayload);

        // Mark as completed on success
        SessionStorageHelper.saveMultipleFields({
          is_completed: true,
          completion_date: new Date().toISOString(),
          assessment_progress: 100,
        });

        return response;
      } catch (error) {
        lastError = error;

        if (attempt < retryCount) {
          const delay = attempt * 1000; // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError;
  },

  // Enhanced debug helper with performance info
  debugPrintData: () => {
    const data = SessionStorageHelper.getAllData();
    const validation = SessionStorageHelper.validateRequiredFields();
    const progress = SessionStorageHelper.getProgress();

    return {
      data,
      validation,
      progress,
      cacheStatus: !!_cache,
      fieldCount: Object.keys(data).length,
    };
  },

  // Utility to reset cache manually
  clearCache: () => {
    _cache = null;
    _lastCacheTime = 0;
  },
};

export default SessionStorageHelper;
